import { c as _c } from "react/compiler-runtime";
import { Box, BoxProps, Button, Separator, Heading, HStack, Image, SimpleGrid, Stack, Text, VStack } from '@chakra-ui/react';
import type { HeroConfigKey } from '@src/components/sco/LayoutBlocks/Hero/configs';
import { HERO_CONFIGS } from '@src/components/sco/LayoutBlocks/Hero/configs';
import type { HeroConfig, HeroHowItWorksCardConfig } from '@src/components/sco/LayoutBlocks/Hero/types';
import { ChevronRightIcon } from '@src/components/sco/icons';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import NextImage from 'next/image';
export type { HeroConfig, HeroConfigKey, HeroHowItWorksCardConfig };
type HeroProps = {
  configKey: HeroConfigKey;
} | {
  config: HeroConfig;
};
function resolveHeroConfig(props: HeroProps): HeroConfig {
  if ('config' in props) {
    return props.config;
  }
  if ('configKey' in props) {
    return HERO_CONFIGS[props.configKey];
  }
  return props;
}
export const Hero = props => {
  const $ = _c(7);
  const config = resolveHeroConfig(props);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const t0 = config.backgroundGradient;
  const t1 = config.backgroundColor;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [2, 2, 6, 10];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== getBreakpointValue) {
    t3 = getBreakpointValue([8, 14]);
    $[1] = getBreakpointValue;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = props.heroBoxProps;
  let t5;
  if ($[3] !== getBreakpointValue) {
    t5 = getBreakpointValue([4, 6, 6]);
    $[3] = getBreakpointValue;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== getBreakpointValue) {
    t6 = getBreakpointValue(["xl", "4xl", "4xl", "4xl", "5xl"]);
    $[5] = getBreakpointValue;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return <Box bg={t0} bgColor={t1} paddingX={t2} paddingY={t3} position="relative" width="full" {...t4}>{config.backgroundImage && <Box inset={0} opacity={0.25} position="absolute" zIndex={0}><NextImage fill={true} alt={typeof config.title === "string" ? config.title : config.backgroundImageAlt ?? ""} src={config.backgroundImage} style={{
        objectFit: "cover"
      }} /></Box>}<VStack align="stretch" gap={10} marginX="auto" maxWidth="container.xl" position="relative" width="full" zIndex={1}><VStack align="center" gap={t5} justify="center" textAlign="center" width="full">{config.customHeaderInsert && config.customHeaderInsert}<Heading as="h1" backgroundClip="text" backgroundImage="linear-gradient(90deg, #d97258 0%, #9858a3 100%)" color="transparent" size={t6}>{config.title}</Heading>{config.description && <Text as="h2" color="red.fg" fontSize={getBreakpointValue(["lg", "lg", "xl", "2xl"])} fontWeight="600" maxWidth="60ch" textStyle="labelLg">{config.description}</Text>}{config.callToAction && <VStack gap={4} marginTop={getBreakpointValue([2, 4, 6])}><Button asChild={true} boxShadow="sm" size={getBreakpointValue(["lg", "lg", "xl", "xl"])} variant="cta"><NextLink href={config.callToAction.href}>{config.callToAction.text}<ChevronRightIcon /></NextLink></Button>{config.callToAction.disclaimerText && <Text color="mocha" maxWidth="60ch" opacity={1} textStyle={getBreakpointValue(["labelXs", "labelSm", "labelSm"])}>{config.callToAction.disclaimerText}</Text>}</VStack>}</VStack>{config.howItWorksCards && config.howItWorksCards.length > 0 && <Stack gap={6} width="full"><HStack gap={6} width="full"><Separator /><Heading color="colorPalette.border" fontWeight="700" letterSpacing="wider" size="lg" textAlign="center" textTransform="uppercase" whiteSpace="nowrap">How it works</Heading><Separator /></HStack><SimpleGrid columns={[1, 1, 3]} gap={4} width="full">{config.howItWorksCards.map(_temp)}</SimpleGrid></Stack>}{config.customContent && config.customContent}</VStack></Box>;
};
const HowItWorksCard = props => {
  const $ = _c(22);
  const {
    getBreakpointValue
  } = useBreakpointContext();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      objectFit: "contain"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <NextImage {...t1 as any} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== props.image || $[4] !== props.title) {
    t3 = <Image asChild={true} alt={props.title} height={180} src={props.image} style={t0} width={240}>{t2}</Image>;
    $[3] = props.image;
    $[4] = props.title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== getBreakpointValue) {
    t4 = getBreakpointValue(["md", "md", "lg", "xl"]);
    $[6] = getBreakpointValue;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== props.title || $[9] !== t4) {
    t5 = <Heading size={t4} textAlign="center">{props.title}</Heading>;
    $[8] = props.title;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== getBreakpointValue) {
    t6 = getBreakpointValue(["sm", "sm", "md", "lg"]);
    $[11] = getBreakpointValue;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== props.description || $[14] !== t6) {
    t7 = <Text fontSize={t6} textAlign="center">{props.description}</Text>;
    $[13] = props.description;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <VStack gap={2}>{t5}{t7}</VStack>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t3 || $[20] !== t8) {
    t9 = <Box borderRadius="2xl" boxShadow="lg" width="full"><VStack align="center" bgColor="colorPalette.container" borderRadius="2xl" boxShadow="inset 0 1px 0 rgba(255, 255, 255, 0.15)" gap={4} height="100%" justify="center" paddingBottom={10} paddingTop={6} paddingX={8} width="full">{t3}{t8}</VStack></Box>;
    $[19] = t3;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};
function _temp(card) {
  return <HowItWorksCard key={card.title} {...card} />;
}